(() => {
  if (customElements.get('pmd-consent')) return;

  const OTHER_BANNERS = '[id^="shopify-pc__"], privacy-banner, .shopify-section--privacy-banner, .giraffly_gdpr, [class^="giraffly_"]';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled])';

  const dropOtherBanners = (root) => {
    if (root.nodeType !== 1) return;
    if (root.matches(OTHER_BANNERS)) root.remove();
    root.querySelectorAll(OTHER_BANNERS).forEach((el) => el.remove());
  };

  const withCustomerPrivacy = (callback) => {
    const shopify = window.Shopify;
    if (!shopify) return;
    if (shopify.customerPrivacy) { callback(shopify.customerPrivacy); return; }
    if (typeof shopify.loadFeatures !== 'function') return;
    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error && shopify.customerPrivacy) callback(shopify.customerPrivacy);
    });
  };

  const hasStoredChoice = (customerPrivacy) => {
    const stored = customerPrivacy.currentVisitorConsent();
    return Boolean(stored.analytics || stored.marketing || stored.preferences);
  };

  const onPolicyPage = (link) => {
    if (!link) return false;
    const trim = (path) => path.replace(/\/+$/, '');
    return trim(link.pathname) === trim(window.location.pathname);
  };

  class PmdConsent extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.designMode = this.hasAttribute('data-design-mode');
      this.reviewMode = new URLSearchParams(window.location.search).get('pmd_consent') === 'show';
      this.dialog = this.querySelector('.pmd-consent__dialog');
      this.panel = this.querySelector('.pmd-consent__panel');
      this.settingsToggle = this.querySelector('[data-pmd-consent="settings"]');
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.trapFocus(event));

      dropOtherBanners(document.documentElement);
      new MutationObserver((records) => {
        for (const record of records) record.addedNodes.forEach(dropOtherBanners);
      }).observe(document.body, { childList: true });

      if (this.designMode || this.reviewMode) { this.show(); return; }
      if (onPolicyPage(this.querySelector('.pmd-consent__link'))) return;
      withCustomerPrivacy((customerPrivacy) => {
        if (customerPrivacy.shouldShowBanner() && !hasStoredChoice(customerPrivacy)) this.show();
      });
    }

    onClick(event) {
      const action = event.target.closest('[data-pmd-consent]')?.dataset.pmdConsent;
      if (action === 'settings') this.toggleSettings();
      else if (action === 'accept') this.save({ analytics: true, marketing: true });
      else if (action === 'reject') this.save({ analytics: false, marketing: false });
      else if (action === 'save') {
        this.save({
          analytics: this.querySelector('[data-pmd-purpose="analytics"]').checked,
          marketing: this.querySelector('[data-pmd-purpose="marketing"]').checked,
        });
      }
    }

    toggleSettings() {
      const open = this.panel.hidden;
      this.panel.hidden = !open;
      this.settingsToggle.setAttribute('aria-expanded', String(open));
      if (open) this.panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    save({ analytics, marketing }) {
      if (this.designMode) return;
      if (this.reviewMode) { this.hide(); return; }
      withCustomerPrivacy((customerPrivacy) => {
        customerPrivacy.setTrackingConsent(
          { analytics, marketing, preferences: analytics, sale_of_data: marketing },
          () => this.hide(),
        );
      });
    }

    show() {
      this.returnFocus = document.activeElement;
      this.hidden = false;
      if (!this.designMode) document.documentElement.classList.add('pmd-consent-lock');
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.dialog.focus({ preventScroll: true });
      });
    }

    hide() {
      document.documentElement.classList.remove('pmd-consent-lock');
      this.classList.remove('is-open');
      const done = () => { this.hidden = true; };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else setTimeout(done, 200);
      if (this.returnFocus && this.returnFocus.focus) this.returnFocus.focus({ preventScroll: true });
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const items = [...this.dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('pmd-consent', PmdConsent);
})();
